import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import PointerRing from "./PointerRing";
import { bookServicePath, business, primaryNav, secondaryNav, socialProfiles } from "../siteData";

const logo = "/assets/images/PPM_LOGO_INVERTED_2000_x_1000_px_-1760923625502.png";
const mark = "/assets/images/4-1761111487827.png";

export function SiteHeader() {
  const [open, setOpen] = useState(false);
  // The header has no border and no background at rest, so nothing separates it
  // from the hero. It earns a ground only once content is running underneath it,
  // which is the only moment legibility needs one.
  const [lifted, setLifted] = useState(false);
  const location = useLocation();

  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    const close = (event) => event.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", close);
    return () => window.removeEventListener("keydown", close);
  }, []);
  useEffect(() => {
    const onScroll = () => setLifted(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header className={`site-header${lifted ? " lifted" : ""}${open ? " menu-open" : ""}`}>
      <div className="header-inner">
        <Link className="brand" to="/" aria-label="Prime Path Motors home">
          <img src={mark} alt="" />
          {/* The wordmark is one line in the approved lockup. Do not stack it. */}
          <span>Prime Path Motors</span>
        </Link>
        <nav className="desktop-nav" aria-label="Primary navigation">
          {primaryNav.map((item) => (
            <NavLink key={item.to} to={item.to} end={item.to === "/"} className={({ isActive }) => isActive ? "active" : undefined}>
              {item.label}
            </NavLink>
          ))}
        </nav>
        <Link className="button button-gold desktop-book" to={bookServicePath}>Book Service</Link>
        <button
          className="menu-button"
          type="button"
          aria-expanded={open}
          aria-controls="mobile-navigation"
          aria-label={open ? "Close navigation" : "Open navigation"}
          onClick={() => setOpen((value) => !value)}
        >
          <span />
          <span />
        </button>
      </div>
      <nav id="mobile-navigation" className={`mobile-nav ${open ? "open" : ""}`} aria-label="Mobile navigation">
        {primaryNav.map((item, index) => (
          <NavLink key={item.to} to={item.to} end={item.to === "/"}>
            <span>{String(index + 1).padStart(2, "0")}</span>{item.label}
          </NavLink>
        ))}
        <Link className="button button-gold" to={bookServicePath}>Book Service</Link>
      </nav>
    </header>
  );
}

export function SiteFooter() {
  return (
    <footer className="site-footer">
      <div className="footer-grid">
        <div className="footer-brand">
          <img src={logo} alt="Prime Path Motors" />
          <p>Premium pre-owned vehicles, sourced to your spec and delivered nationwide. Automotive service and tire sales in Alvarado, Texas.</p>
        </div>
        <div>
          <p className="footer-label">Visit</p>
          <address>{business.addressLine1}<br />{business.addressLine2}</address>
          <a href={`tel:${business.phoneHref}`}>{business.phoneDisplay}</a>
          <a href={`mailto:${business.email}`}>{business.email}</a>
        </div>
        <div>
          <p className="footer-label">Navigate</p>
          {primaryNav.map((item) => <Link key={item.to} to={item.to}>{item.label}</Link>)}
        </div>
        <div>
          {/* The request pages are not in the top nav, so this is the only place
              a visitor can reach the trade appraisal without going via the home
              page. It was previously reachable from one band on the homepage. */}
          <p className="footer-label">Start a request</p>
          {secondaryNav.map((item) => <Link key={item.to} to={item.to}>{item.label}</Link>)}
        </div>
        <div>
          <p className="footer-label">Hours</p>
          {business.hours.map((line) => <p key={line}>{line}</p>)}
        </div>
      </div>
      {/* Own-brand profiles. rel="me" states that the site and the profile are
          the same entity, which is what a verifier reads; noopener is standard
          for any target=_blank. */}
      <nav className="footer-social" aria-label="Prime Path Motors on social media">
        {socialProfiles.map((s) => (
          <a key={s.label} href={s.url} target="_blank" rel="me noopener noreferrer">{s.label}</a>
        ))}
      </nav>

      <div className="footer-base">
        <p>© {new Date().getFullYear()} Prime Path Motors · {business.legalLine}</p>
        <div><Link to="/privacy">Privacy</Link><Link to="/terms">Terms</Link></div>
      </div>
    </footer>
  );
}

export default function SiteShell({ children }) {
  return (
    <>
      <a className="skip-link" href="#main-content">Skip to content</a>
      <PointerRing />
      <SiteHeader />
      <main id="main-content">{children}</main>
      <SiteFooter />
    </>
  );
}
